import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import Button from '../ui/Button';
import EmptyState from '../ui/EmptyState';
import ErrorState from '../ui/ErrorState';
import Switch from '../ui/Switch';
import Tabs from '../ui/Tabs';

const ago = iso => {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// The shell's notifications panel (the old bell's panel on src/ui). Opens beside the sidebar foot.
// n: the useNotifications() result. Escape and an outside click close it; focus returns to the opener.
export default function NotificationsPanel({ n, onClose, railOffset }) {
  const [filter, setFilter] = useState('all');
  const ref = useRef(null);
  useEffect(() => {
    const back = document.activeElement;
    ref.current?.querySelector('[role="tab"]')?.focus();
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    const onDown = e => { if (!ref.current?.contains(e.target) && !e.target.closest?.('.ui-sb__foot')) onClose(); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); back?.focus?.(); };
  }, [onClose]);
  const list = n.filtered(filter);
  return (
    <section className="ui ui-notif" role="dialog" aria-label="Notifications" ref={ref} style={{ left: railOffset }}>
      <header className="ui-notif__head">
        <h2 className="ui-notif__title">Notifications</h2>
        {n.unread > 0 && <Button variant="quiet" onClick={n.markAllRead}>Mark all read</Button>}
        <Button variant="quiet" iconOnly icon={X} label="Close notifications" onClick={onClose} />
      </header>
      <div className="ui-notif__bar">
        <Tabs label="Notification filter" value={filter} onChange={setFilter}
          items={[{ id: 'all', label: 'All', count: n.notifications.length }, { id: 'scripts', label: 'Scripts' }, { id: 'videos', label: 'Videos' }]} />
        <Switch label="On" checked={n.enabled} onChange={n.toggleEnabled} aria-label="Notifications on" />
      </div>
      <div className="ui-notif__list">
        {n.error && <ErrorState title="Notifications didn't load" message="The server didn't answer. They refresh every 30 seconds; retry now if you're waiting on one." onRetry={n.refresh} />}
        {!n.error && n.loaded && list.length === 0 && <EmptyState message={filter === 'all' ? 'Nothing new. You hear about scripts, cards and comments here.' : 'Nothing in this filter. Switch to All to see everything.'} />}
        {list.map(item => (
          <button key={item.id} type="button" className="ui-notif__item" data-unread={!item.isRead || undefined}
            onClick={async () => { if (await n.open(item)) onClose(); }}>
            <span className="ui-notif__main"><span className="ui-notif__it">{item.title}</span><span className="ui-notif__body">{item.body}</span></span>
            <span className="ui-meta">{ago(item.createdAt)}</span>
          </button>
        ))}
      </div>
    </section>
  );
}
